import type { SidebarSortableTask } from "./taskSorting"
import { shouldSurfaceSidebarTaskUnread } from "./taskUnread"

const recentClosedWindowMs = 2 * 24 * 60 * 60 * 1000
const recentClosedLimit = 10

interface SidebarEntryPartition<T> {
    allClosedEntries: T[]
    recentClosedEntries: T[]
    topEntries: T[]
}

interface SidebarEntryPartitionOptions {
    retainedTopClosedEntryId?: string
}

function closedAtMs(entry: SidebarSortableTask): number {
    const value = Date.parse(entry.closedAt ?? entry.updatedAt ?? entry.createdAt)
    return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY
}

export function partitionSidebarEntries<T extends SidebarSortableTask>(
    entries: T[],
    now = new Date(),
    options: SidebarEntryPartitionOptions = {}
): SidebarEntryPartition<T> {
    const scheduledClosedEntries: T[] = []
    const ordinaryClosedEntries: T[] = []
    const topEntries: T[] = []
    const recentThreshold = now.getTime() - recentClosedWindowMs

    for (const entry of entries) {
        if (!entry.closed || entry.id === options.retainedTopClosedEntryId || shouldSurfaceSidebarTaskUnread(entry, now)) {
            topEntries.push(entry)
        } else if ((entry.scheduledCount ?? 0) > 0) {
            scheduledClosedEntries.push(entry)
        } else {
            ordinaryClosedEntries.push(entry)
        }
    }

    const recentIds = new Set(
        ordinaryClosedEntries
            .filter((entry) => closedAtMs(entry) >= recentThreshold)
            .sort((left, right) => closedAtMs(right) - closedAtMs(left))
            .slice(0, recentClosedLimit)
            .map((entry) => entry.id)
    )
    const allClosedEntries = [...scheduledClosedEntries, ...ordinaryClosedEntries]
    const recentClosedEntries = [...scheduledClosedEntries, ...ordinaryClosedEntries.filter((entry) => recentIds.has(entry.id))]

    return { allClosedEntries, recentClosedEntries, topEntries }
}
